import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { LifeBuoy, Loader2 } from "lucide-react";
import { toast } from "sonner";

const errMsg = (e) => e?.response?.data?.detail || e.message;

function OrphanRow({ o, techs, onDone }) {
  const [target, setTarget] = useState(o.suggested_new_id || "");
  const [busy, setBusy] = useState(false);
  async function restore() {
    const name = techs.find((t) => t.id === target)?.name;
    if (!window.confirm(`Pindahkan ${o.wo_count} WO, ${o.assignments} kru harian, dan ${o.gps_points} titik GPS ke ${name}?`)) return;
    setBusy(true);
    try {
      const r = await api.armadaRecoveryRemap(o.old_id, target);
      toast.success(`Dipulihkan ke ${r.new_name}: ${r.work_orders} WO · ${r.assignments} kru harian · ${r.gps_points} titik GPS`);
      if (r.assignment_conflicts?.length) toast.warning(`Kru harian tanggal ${r.assignment_conflicts.join(", ")} dilewati karena ${r.new_name} sudah punya kru di tanggal itu`);
      onDone();
    } catch (e) { toast.error("Gagal memulihkan: " + errMsg(e)); }
    finally { setBusy(false); }
  }
  return (
    <div className="border border-zinc-200 rounded-sm p-3 space-y-2" data-testid={`armada-orphan-${o.old_id}`}>
      <div className="flex flex-wrap items-baseline gap-x-3 text-[12px]">
        <span className="font-mono text-[11px] text-zinc-500">ID lama {o.old_id.slice(0, 13)}…</span>
        <b className="text-zinc-900">{o.wo_count} WO</b>
        <span className="text-zinc-600">{o.first} s/d {o.last}</span>
        <span className="text-zinc-500">{o.assignments} kru harian · {o.gps_points} titik GPS</span>
      </div>
      {o.sample_addresses.length > 0 && <div className="text-[11px] text-zinc-500 truncate">Contoh alamat: {o.sample_addresses.join(" · ")}</div>}
      {o.calendar_ids.length > 0 && <div className="text-[11px] text-zinc-500 truncate">Kalender: {o.calendar_ids.join(", ")}</div>}
      <div className="flex items-center gap-2">
        <Select value={target} onValueChange={setTarget}>
          <SelectTrigger className="h-8 w-56 rounded-sm text-[12px]" data-testid={`armada-orphan-target-${o.old_id}`}>
            <SelectValue placeholder="Pilih tim tujuan" />
          </SelectTrigger>
          <SelectContent>
            {techs.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}{t.id === o.suggested_new_id ? " (disarankan)" : ""}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" className="h-8 rounded-sm" disabled={!target || busy} onClick={restore} data-testid={`armada-orphan-restore-${o.old_id}`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Pulihkan"}
        </Button>
      </div>
    </div>
  );
}

export function ArmadaRecoveryButton({ onRestored }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const load = () => api.armadaRecoveryOrphans().then(setData).catch((e) => toast.error("Gagal memuat: " + errMsg(e)));
  const openDialog = () => { setOpen(true); setData(null); load(); };
  return (
    <>
      <Button variant="outline" onClick={openDialog} className="rounded-sm gap-1.5" data-testid="armada-recovery-btn">
        <LifeBuoy className="w-4 h-4" />Pulihkan WO armada terhapus
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-sm" data-testid="armada-recovery-dialog">
          <DialogHeader><DialogTitle>Pulihkan WO armada terhapus</DialogTitle></DialogHeader>
          <p className="text-[12px] text-zinc-500">
            Daftar ID tim armada yang sudah dihapus tetapi masih punya WO. Pilih tim tujuan (disarankan berdasarkan kalender Gmail yang sama), lalu tekan Pulihkan.
          </p>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {!data && <div className="text-[12px] text-zinc-500"><Loader2 className="w-4 h-4 animate-spin inline mr-1" />Memuat…</div>}
            {data && data.orphans.length === 0 && <div className="text-[12px] text-emerald-700" data-testid="armada-recovery-empty">Tidak ada WO dari armada terhapus.</div>}
            {data?.orphans.map((o) => <OrphanRow key={o.old_id} o={o} techs={data.technicians} onDone={() => { load(); onRestored?.(); }} />)}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
